import { thermalCategories, thermalProductsByCategory, thermalProductPath } from '../data/thermal';
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuoteList } from '../lib/quoteList';
import { buildPartsMessage } from './UL101QuoteTray';

const Header = ({ onRequestDemo, onRequestQuote }) => {
  const { list: quoteList } = useQuoteList();
  const quoteCount = Object.keys(quoteList).length;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSolutionsDropdownOpen, setIsSolutionsDropdownOpen] = useState(false);
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [isSupportDropdownOpen, setIsSupportDropdownOpen] = useState(false);
  // Accordion state for the "Acoustic Imagers" sub-tree inside the
  // Solutions dropdown — mirrored between desktop and mobile menus.
  const [isImagersOpen, setIsImagersOpen] = useState(false);
  // Accordion state for the "Aviation Headsets and GSE Team Kits"
  // (Dalcomm Tech) sub-tree — same pattern as Acoustic Imagers.
  const [isDalcommOpen, setIsDalcommOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({});
  const toggleGroup = (slug) => setOpenGroups((g) => ({ ...g, [slug]: !g[slug] }));
  const thermalGroups = Object.values(thermalCategories).map((c) => ({
    slug: c.slug,
    name: c.name,
    path: c.path,
    children: thermalProductsByCategory(c.slug).map((p) => ({ name: p.menuName, path: thermalProductPath(p) })),
  }));
  const renderGroup = (g, mobile) => {
    const open = !!openGroups[g.slug];
    const close = () => (mobile ? setIsMobileMenuOpen(false) : setIsSolutionsDropdownOpen(false));
    const tid = `${mobile ? 'mobile-' : ''}nav-solutions-${g.slug}`;
    return (
      <React.Fragment key={g.slug}>
        <div className={mobile ? 'flex items-stretch rounded-lg overflow-hidden' : `${g.slug === 'ul101' ? '' : 'border-t border-slate-100 mt-1 pt-1 '}flex items-stretch`}>
          <Link
            to={g.path}
            onClick={close}
            className={mobile
              ? 'flex-1 text-left text-base font-normal text-slate-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-2.5 transition-colors duration-200'
              : 'flex-1 px-4 py-2.5 text-slate-700 font-medium hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 text-sm'}
            data-testid={tid}
          >
            {g.name}
          </Link>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleGroup(g.slug); }}
            aria-expanded={open}
            aria-controls={`${tid}-children`}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${g.name} submenu`}
            data-testid={`${tid}-caret`}
            className={`${mobile ? 'px-4' : 'px-3'} text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex items-center`}
          >
            <ChevronDown className={`${mobile ? 'h-5 w-5' : 'h-4 w-4'} transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {open && (
          <div id={`${tid}-children`} className={mobile ? 'space-y-1 pb-1' : 'bg-slate-50/60'}>
            {g.children.map((child, index) => (
              <Link
                key={child.path}
                to={child.path}
                onClick={close}
                className={mobile
                  ? 'block w-full text-left text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 pl-9 pr-4 py-2 rounded-lg transition-colors duration-200'
                  : 'block pl-9 pr-4 py-2 text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 text-sm'}
                data-testid={`${tid}-child-${index}`}
              >
                {child.name}
              </Link>
            ))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const ul101 = {
    slug: 'ul101',
    name: 'UL101 Ultrasonic Troubleshooter (B00033)',
    path: '/products/ul101',
    children: [
      { name: 'UL101 Aviation Troubleshooter Kit (B00033)', path: '/products/ul101' },
      { name: 'UL101 Replacement Parts', path: '/products/ul101#kit-parts' },
      { name: 'UT2000 Universal Transmitter (026012)', path: '/products/ul101#part-026012' },
      { name: 'UT2000 Cabin Multi-Pak — Set of 4 (026305)', path: '/products/ul101#part-026305' },
      { name: 'UT2000 Cabin Multi-Pak — Set of 8 (026310)', path: '/products/ul101#part-026310' },
    ],
  };
  const comboKits = {
    slug: 'combo-kits',
    name: 'UL101 + Acoustic Camera Combo Kits',
    path: '/products/combo-kits',
    children: [
      { name: 'B00700 — UL101 Ultrasonic Inspection and Listening Kit (H6)', path: '/products/combo-kits/b00700' },
      { name: 'B00705 — UL101 Compact Ultrasonic Inspection and Imaging Kit (H4mini)', path: '/products/combo-kits/b00705' },
    ],
  };
  const acousticImagers = { name: 'Acoustic Cameras', path: '/products/fotric' };
  const acousticImagersChildren = [
    { name: 'H4mini', path: '/products/acoustic-cameras/h4mini' },
    { name: 'H-Series (H4 / H6)', path: '/products/acoustic-cameras/h-series' },
    { name: 'H-Flex', path: '/products/acoustic-cameras/h-flex' },
    { name: 'H7', path: '/products/acoustic-cameras/h7' },
  ];

  const dalcomm = { name: 'Aviation Headsets and GSE Team Kits', path: '/products/dalcomm' };
  const dalcommProducts = [
    { name: 'Wireless Intercom Kit', path: '/products/dalcomm/wireless-intercom-kit' },
    { name: 'Embry-Riddle Aviation Headset', path: '/products/dalcomm/embry-riddle-aviation-headset' },
    { name: 'J1 Carbon Fiber Pilot’s Headset', path: '/products/dalcomm/j1-carbon-fiber-pilots-headset' },
    { name: 'Vintage Aviation Headset', path: '/products/dalcomm/vintage-aviation-headset' },
    { name: 'GSE Adapters & Accessories', path: '/products/dalcomm/gse-adapters' },
  ];

  const companyLinks = [
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/#contact' },
    { name: 'Distribution', path: '/distribution' },
    { name: 'Investors', path: '/investors' },
    { name: 'News', path: '/news' },
    { name: 'Press Kit', path: '/press' }
  ];

  const supportLinks = [
    { name: 'Calibration & RMA', path: '/support/calibration-rma' },
    { name: 'Documentation', path: '/support/documentation' },
    { name: 'Warranty', path: '/support/warranty' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#1e2d3b]/95 backdrop-blur-md shadow-md' 
          : 'bg-[#1e2d3b]/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="hover:opacity-80 transition-opacity duration-200"
            data-testid="header-logo-link"
          >
            <img 
              src="/images/logo-for-website.png"
              alt="CTRL Systems"
              className="h-12 sm:h-14 lg:h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {/* Company Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsCompanyDropdownOpen(true)}
              onMouseLeave={() => setIsCompanyDropdownOpen(false)}
            >
              <Link
                to="/about"
                className="flex items-center space-x-1 text-white hover:text-blue-400 font-medium transition-colors duration-200"
                data-testid="nav-company-trigger"
                aria-haspopup="true"
                aria-expanded={isCompanyDropdownOpen}
              >
                <span>Company</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isCompanyDropdownOpen ? 'rotate-180' : ''}`} />
              </Link>
              
              {/* Dropdown Menu */}
              {isCompanyDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 w-64 z-50">
                  <div className="bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                  {companyLinks.map((link, index) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsCompanyDropdownOpen(false)}
                      className="block px-4 py-3 text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 text-sm"
                      data-testid={`nav-company-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Solutions Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsSolutionsDropdownOpen(true)}
              onMouseLeave={() => setIsSolutionsDropdownOpen(false)}
            >
              <Link
                to="/products/ul101"
                className="flex items-center space-x-1 text-white hover:text-blue-400 font-medium transition-colors duration-200"
                data-testid="nav-solutions-trigger"
                aria-haspopup="true"
                aria-expanded={isSolutionsDropdownOpen}
              >
                <span>GSTE Products</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isSolutionsDropdownOpen ? 'rotate-180' : ''}`} />
              </Link>
              
              {/* Dropdown Menu */}
              {isSolutionsDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 w-96 z-50">
                  <div className="bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                    {renderGroup(ul101, false)}

                    {renderGroup(comboKits, false)}

                    {/* Acoustic Cameras parent row with accordion caret */}
                    <div className="border-t border-slate-100 mt-1 pt-1 flex items-stretch">
                      <Link
                        to={acousticImagers.path}
                        onClick={() => setIsSolutionsDropdownOpen(false)}
                        className="flex-1 px-4 py-2.5 text-slate-700 font-medium hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 text-sm"
                        data-testid="nav-solutions-acoustic-imagers"
                      >
                        {acousticImagers.name}
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setIsImagersOpen((v) => !v); }}
                        aria-expanded={isImagersOpen}
                        aria-controls="nav-solutions-imagers-children"
                        aria-label={isImagersOpen ? 'Collapse Acoustic Cameras submenu' : 'Expand Acoustic Cameras submenu'}
                        data-testid="nav-solutions-imagers-caret"
                        className="px-3 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex items-center"
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${isImagersOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </div>

                    {/* Accordion children */}
                    {isImagersOpen && (
                      <div
                        id="nav-solutions-imagers-children"
                        className="bg-slate-50/60"
                      >
                        {acousticImagersChildren.map((child, index) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setIsSolutionsDropdownOpen(false)}
                            className="block pl-9 pr-4 py-2 text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 text-sm"
                            data-testid={`nav-solutions-imagers-child-${index}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}

                    {thermalGroups.map((g) => renderGroup(g, false))}
                    {/* Aviation Headsets and GSE Team Kits (Dalcomm) parent
                        row with accordion caret — same pattern as Acoustic
                        Imagers above. */}
                    <div className="border-t border-slate-100 mt-1 pt-1 flex items-stretch">
                      <Link
                        to={dalcomm.path}
                        onClick={() => setIsSolutionsDropdownOpen(false)}
                        className="flex-1 px-4 py-2.5 text-slate-700 font-medium hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 text-sm"
                        data-testid="nav-solutions-dalcomm"
                      >
                        {dalcomm.name}
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setIsDalcommOpen((v) => !v); }}
                        aria-expanded={isDalcommOpen}
                        aria-controls="nav-solutions-dalcomm-children"
                        aria-label={isDalcommOpen ? 'Collapse Aviation Headsets submenu' : 'Expand Aviation Headsets submenu'}
                        data-testid="nav-solutions-dalcomm-caret"
                        className="px-3 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex items-center"
                      >
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${isDalcommOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                    </div>

                    {isDalcommOpen && (
                      <div
                        id="nav-solutions-dalcomm-children"
                        className="bg-slate-50/60"
                      >
                        {dalcommProducts.map((child, index) => (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={() => setIsSolutionsDropdownOpen(false)}
                            className="block pl-9 pr-4 py-2 text-slate-600 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 text-sm"
                            data-testid={`nav-solutions-dalcomm-child-${index}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Support Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsSupportDropdownOpen(true)}
              onMouseLeave={() => setIsSupportDropdownOpen(false)}
            >
              <Link
                to="/support/documentation"
                className="flex items-center space-x-1 text-white hover:text-blue-400 font-medium transition-colors duration-200"
                data-testid="nav-support-trigger"
                aria-haspopup="true"
                aria-expanded={isSupportDropdownOpen}
              >
                <span>Support</span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isSupportDropdownOpen ? 'rotate-180' : ''}`} />
              </Link>
              
              {/* Dropdown Menu */}
              {isSupportDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 w-64 z-50">
                  <div className="bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                  {supportLinks.map((link, index) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsSupportDropdownOpen(false)}
                      className="block px-4 py-3 text-slate-700 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-200 text-sm"
                      data-testid={`nav-support-${link.name.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                  </div>
                </div>
              )}
            </div>
            
            <Link
              to="/use-cases"
              className="text-white hover:text-blue-400 font-medium transition-colors duration-200"
              data-testid="nav-use-cases"
            >
              Use Cases
            </Link>
          </nav>

          {/* Right side: CTA Buttons + Hamburger Menu */}
          <div className="flex items-center space-x-4">
            {/* CTA Buttons - visible on tablet and up */}
            <div className="hidden md:flex items-center space-x-4">
              <Button 
                onClick={onRequestDemo}
                variant="outline"
                data-testid="header-request-demo-btn"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 transition-colors duration-200"
              >
                Request Demo
              </Button>
              <Button 
                onClick={() => onRequestQuote(quoteCount ? { resource: 'header-quote-list', prefill: { product: 'UL101', message: buildPartsMessage(quoteList) } } : { resource: 'header' })}
                data-testid="header-request-quote-btn"
                title={quoteCount ? `${quoteCount} part${quoteCount === 1 ? '' : 's'} waiting in your quote list` : undefined}
                className="relative bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white transition-all duration-200 shadow-lg shadow-orange-500/30"
              >
                Request Quote
                {quoteCount > 0 && (
                  <span className="absolute -top-2 -right-2 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[#1e2d3b] px-1.5 text-[11px] font-bold text-white ring-2 ring-white animate-fade-in" aria-label={`${quoteCount} parts in quote list`} data-testid="header-quote-badge">
                    {quoteCount}
                  </span>
                )}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white hover:text-blue-400 transition-colors duration-200"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-8 space-y-8">
            {/* Company Section */}
            <div>
              <h3 className="text-sm font-bold text-[#1e2d3b] uppercase tracking-wider mb-4">Company</h3>
              <div className="space-y-2">
                {companyLinks.map((link, index) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-left text-slate-600 hover:text-blue-600 hover:bg-blue-50 px-4 py-2.5 rounded-lg transition-colors duration-200 text-base font-normal"
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-200"></div>

            {/* Solutions Section */}
            <div>
              <h3 className="text-sm font-bold text-[#1e2d3b] uppercase tracking-wider mb-4">GSTE Products</h3>
              <div className="space-y-1">
                {renderGroup(ul101, true)}

                {renderGroup(comboKits, true)}

                {/* Acoustic Cameras parent row with accordion caret */}
                <div className="flex items-stretch rounded-lg overflow-hidden">
                  <Link
                    to={acousticImagers.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex-1 text-left text-base font-normal text-slate-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-2.5 transition-colors duration-200 leading-snug"
                    data-testid="mobile-nav-solutions-acoustic-imagers"
                  >
                    {acousticImagers.name}
                  </Link>
                  <button
                    type="button"
                    onClick={() => setIsImagersOpen((v) => !v)}
                    aria-expanded={isImagersOpen}
                    aria-controls="mobile-nav-imagers-children"
                    aria-label={isImagersOpen ? 'Collapse Acoustic Cameras submenu' : 'Expand Acoustic Cameras submenu'}
                    data-testid="mobile-nav-solutions-imagers-caret"
                    className="px-4 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex items-center"
                  >
                    <ChevronDown
                      className={`h-5 w-5 transition-transform duration-200 ${isImagersOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>

                {isImagersOpen && (
                  <div
                    id="mobile-nav-imagers-children"
                    className="space-y-1 pb-1"
                  >
                    {acousticImagersChildren.map((child, index) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block w-full text-left text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 pl-9 pr-4 py-2 rounded-lg transition-colors duration-200 leading-snug"
                        data-testid={`mobile-nav-solutions-imagers-child-${index}`}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}

                {thermalGroups.map((g) => renderGroup(g, true))}
                {/* Aviation Headsets and GSE Team Kits (Dalcomm) parent row
                    with accordion caret — mirrors the Acoustic Imagers row
                    above. */}
                <div className="flex items-stretch rounded-lg overflow-hidden">
                  <Link
                    to={dalcomm.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex-1 text-left text-base font-normal text-slate-700 hover:text-blue-600 hover:bg-blue-50 px-4 py-2.5 transition-colors duration-200 leading-snug"
                    data-testid="mobile-nav-solutions-dalcomm"
                  >
                    {dalcomm.name}
                  </Link>
                  <button
                    type="button"
                    onClick={() => setIsDalcommOpen((v) => !v)}
                    aria-expanded={isDalcommOpen}
                    aria-controls="mobile-nav-dalcomm-children"
                    aria-label={isDalcommOpen ? 'Collapse Aviation Headsets submenu' : 'Expand Aviation Headsets submenu'}
                    data-testid="mobile-nav-solutions-dalcomm-caret"
                    className="px-4 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors duration-200 flex items-center"
                  >
                    <ChevronDown
                      className={`h-5 w-5 transition-transform duration-200 ${isDalcommOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>

                {isDalcommOpen && (
                  <div
                    id="mobile-nav-dalcomm-children"
                    className="space-y-1 pb-1"
                  >
                    {dalcommProducts.map((child, index) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block w-full text-left text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 pl-9 pr-4 py-2 rounded-lg transition-colors duration-200 leading-snug"
                        data-testid={`mobile-nav-solutions-dalcomm-child-${index}`}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-200"></div>

            {/* Support Section */}
            <div>
              <h3 className="text-sm font-bold text-[#1e2d3b] uppercase tracking-wider mb-4">Support</h3>
              <div className="space-y-2">
                {supportLinks.map((link, index) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-left text-slate-600 hover:text-blue-600 hover:bg-blue-50 px-4 py-2.5 rounded-lg transition-colors duration-200 text-base font-normal"
                    data-testid={`mobile-nav-support-${index}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-200"></div>

            {/* Use Cases - Category Header that's also a link */}
            <div>
              <Link
                to="/use-cases"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-sm font-bold text-[#1e2d3b] uppercase tracking-wider hover:text-blue-600 transition-colors duration-200"
                data-testid="mobile-nav-use-cases"
              >
                Use Cases
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
